import {
  IonIcon,
  IonLabel,
  IonPage,
  IonRouterOutlet,
  IonTabBar,
  IonTabButton,
} from '@ionic/react'
import { msg } from '@lingui/core/macro'
import { useLingui } from '@lingui/react'
import { personSharp, timeSharp, todaySharp } from 'ionicons/icons'
import { useEffect } from 'react'
import { Route, useHistory, useLocation } from 'react-router-dom'
import {
  TAB_ROOTS,
  TabNavigationProvider,
  tabOf,
  useRequiredTabNavigation,
} from '../context/TabNavigationContext'
import { isKnownPath } from '../helpers/routes'
import { parseShortLinkSlug } from '../helpers/shortLinkSlug'
import { useTimeClock } from '../hooks/timeclock/useTimeClock'
import { useFeature } from '../hooks/useFeatures'
import { useNativeSwipeBackOff } from '../hooks/useNativeSwipeBackOff'
import { AssignmentsPage } from '../pages/AssignmentsPage'
import { ProfilePage } from '../pages/ProfilePage'
import { TimeClockHistoryPage } from '../pages/TimeClockHistoryPage'
import { TimeClockPage } from '../pages/TimeClockPage'
import {
  assignmentDetailRoutes,
  orderPhotosRoutes,
} from './assignmentDetailRoutes'

/**
 * Ionic /:fallback workaround for https://github.com/ionic-team/ionic-framework/issues/23743
 *
 * Redirects imperatively with React Router's `history.replace` (in an effect,
 * not a render-time `<Redirect>`, which corrupts Ionic's navigation stack).
 * `IonRouterOutlet` renders the most specific matching route, so this only runs
 * for paths no dedicated route handled — and we guard known prefixes so a page
 * that is still mounted during an Ionic transition is never bounced. A short
 * single-segment unknown path is treated as a legacy short-link slug.
 */
const FallbackRedirect = () => {
  const history = useHistory()
  const { pathname: path, search, hash } = useLocation()

  useEffect(() => {
    if (isKnownPath(path)) return
    const slug = parseShortLinkSlug(path)
    history.replace(slug ? `/o/${slug}${search}${hash}` : '/assignments')
  }, [path, search, hash, history])

  return null
}

/**
 * Transparent stand-in for a tab root inside the router outlet. Ionic only
 * animates a page transition when the outlet holds a leaving view, so without
 * these the first detail page of a tab appeared with no animation and later
 * ones animated over stale views. The real root (the keep-alive layer) shows
 * through and keeps the taps; see docs/adr/0009-ghost-tab-roots-in-outlet.md.
 */
const TabRootGhost = () => <IonPage className='ka-ghost-root' />

/** One ghost per tab root, so the outlet always has a view to animate from. */
const tabRootGhostRoutes = () =>
  Object.values(TAB_ROOTS).map((path) => (
    <Route key={path} exact path={path} component={TabRootGhost} />
  ))

export const TabLayout = () => {
  // The ghost roots would otherwise hand the edge swipe to Ionic (ADR-0009)
  useNativeSwipeBackOff()
  return (
    <TabNavigationProvider>
      <TabShell />
    </TabNavigationProvider>
  )
}

const TabShell = () => {
  const { _ } = useLingui()
  const history = useHistory()
  // Tab taps restore the tab's last page (per-tab memory).
  const { openTab } = useRequiredTabNavigation()
  // Drive the active tab from the URL ourselves. Ionic's auto-detection doesn't
  // fire reliably here because the tab shell sits under a top-level <Switch>
  // (not an IonRouterOutlet), which left no tab highlighted on launch.
  const { pathname } = useLocation()
  const timeclocksEnabled = useFeature('timeclocksEnabled')
  const { status: timeClockStatus } = useTimeClock({
    pause: !timeclocksEnabled,
    poll: true,
  })
  const clockedIn = timeClockStatus.isActive
  const selectedTab = tabOf(pathname)

  useEffect(() => {
    if (!timeclocksEnabled && pathname.startsWith('/timeclock')) {
      history.replace('/assignments')
    }
  }, [timeclocksEnabled, pathname, history])
  const layer = (active: boolean) => ({
    position: 'absolute' as const,
    inset: 0,
    // display:none (not visibility) keeps the component mounted (no remount =
    // no flicker) while fully hiding it — Ionic sets visibility:visible on
    // IonContent, which would defeat visibility:hidden.
    display: active ? ('block' as const) : ('none' as const),
  })
  return (
    <>
      {/* Handles '/' → /assignments and unknown slugs via history.replace. */}
      <FallbackRedirect />
      {/* Persistent main tab pages: kept mounted and toggled with display so
          switching tabs never unmounts/remounts them. Ionic 8 remounts routed
          tab pages, leaving the outlet blank for a frame — that's the flicker.
          Keeping them alive removes it entirely. */}
      <div className='ka-tab-shell'>
        <div style={layer(selectedTab === 'assignments')}>
          <AssignmentsPage />
        </div>
        {timeclocksEnabled && (
          <div style={layer(selectedTab === 'timeclock')}>
            <TimeClockPage />
          </div>
        )}
        <div style={layer(selectedTab === 'profile')}>
          <ProfilePage />
        </div>
      </div>
      {/* Detail/overlay pages render on top via the router outlet. `ka-tab-shell`
          reserves the tab-bar height so the detail page's content isn't hidden
          behind the (manual) tab bar. */}
      <IonRouterOutlet id='primary' className='ka-tab-shell ka-outlet'>
        {tabRootGhostRoutes()}
        {assignmentDetailRoutes()}
        {orderPhotosRoutes()}
        {/* Always a Route: a `false` child crashes Ionic's view stack (child.props on null),
            blanking the app for tenants without time clocks. The effect above already
            redirects /timeclock* when the module is off. */}
        <Route
          exact
          path='/timeclock/history'
          component={TimeClockHistoryPage}
        />
      </IonRouterOutlet>
      <IonTabBar slot='bottom' selectedTab={selectedTab} className='ka-tabbar'>
        <IonTabButton
          tab='assignments'
          selected={selectedTab === 'assignments'}
          onClick={() => openTab('assignments')}
        >
          <IonIcon icon={todaySharp} size='small' />
          <IonLabel>{_(msg`Assignments`)}</IonLabel>
        </IonTabButton>
        {timeclocksEnabled && (
          <IonTabButton
            tab='timeclock'
            selected={selectedTab === 'timeclock'}
            onClick={() => openTab('timeclock')}
          >
            <IonIcon
              icon={clockedIn ? '/assets/time-sharp-active.svg' : timeSharp}
              style={{ fontSize: '1.35rem' }}
            />
            <IonLabel>{_(msg`Time Clock`)}</IonLabel>
          </IonTabButton>
        )}
        <IonTabButton
          tab='profile'
          selected={selectedTab === 'profile'}
          onClick={() => openTab('profile')}
        >
          <IonIcon icon={personSharp} size='small' />
          <IonLabel>{_(msg`Profile`)}</IonLabel>
        </IonTabButton>
      </IonTabBar>
    </>
  )
}
